// src/AudioSpectrum/ControlPanel.js

import React from 'react';
import { Play, Pause, Square, Volume2, Settings } from 'lucide-react';
import './ControlPanel.css';

const ControlPanel = ({
  onPlay,
  onPause,
  onStop,
  onSeek,
  onVolumeChange,
  onFftSizeChange,
  onVisualizationTypeChange,
  isPlaying,
  currentTime,
  duration,
  volume,
  fftSize,
  visualizationType,
  language
}) => {
  // 시각화 타입 데이터 정의
  const visualizationTypes = [
    // { value: 'bars', label: { KR: '세로 바', EN: 'Bars' }, icon: '▌', description: { KR: '기본 세로 막대 차트', EN: 'Basic vertical bar chart' } },
    // { value: 'horizontalBars', label: { KR: '가로 바', EN: 'H-Bars' }, icon: '▬', description: { KR: '가로 막대 차트', EN: 'Horizontal bar chart' } },
    // { value: 'mirrorBars', label: { KR: '미러 바', EN: 'Mirror' }, icon: '▌▌', description: { KR: '대칭 막대 차트', EN: 'Mirrored bar chart' } },
    // { value: 'stackedBars', label: { KR: '적층 바', EN: 'Stack' }, icon: '▦', description: { KR: '적층된 막대 차트', EN: 'Stacked bar chart' } },
    { value: 'roundedBars', label: { KR: '둥근 바', EN: 'Round' }, icon: '◗', description: { KR: '둥근 모서리 막대', EN: 'Rounded corner bars' } },
    { value: 'gradientBars', label: { KR: '그라디언트', EN: 'Gradient' }, icon: '◐', description: { KR: '그라디언트 막대', EN: 'Gradient bars' } },
    { value: 'circular', label: { KR: '원형', EN: 'Circle' }, icon: '◉', description: { KR: '원형 스펙트럼', EN: 'Circular spectrum' } },
    { value: 'circularWave', label: { KR: '원형 파동', EN: 'C-Wave' }, icon: '〰', description: { KR: '원형 파동', EN: 'Circular wave' } },
    { value: 'spiral', label: { KR: '나선형', EN: 'Spiral' }, icon: '🌀', description: { KR: '나선형 패턴', EN: 'Spiral pattern' } },
    { value: 'waveform', label: { KR: '파형', EN: 'Wave' }, icon: '∿', description: { KR: '시간 도메인 파형', EN: 'Time domain waveform' } },
    // { value: 'oscilloscope', label: { KR: '오실로스코프', EN: 'Scope' }, icon: '◎', description: { KR: 'XY 오실로스코프', EN: 'XY oscilloscope' } },
    { value: 'spectrogram', label: { KR: '스펙트로그램', EN: 'Spectro' }, icon: '▣', description: { KR: '시간-주파수 분석', EN: 'Time-frequency analysis' } },
    { value: 'waterfall', label: { KR: '폭포수', EN: 'Waterfall' }, icon: '≡', description: { KR: '폭포수 효과', EN: 'Waterfall effect' } },
    { value: 'particles', label: { KR: '파티클', EN: 'Particle' }, icon: '∘', description: { KR: '파티클 시스템', EN: 'Particle system' } },
    // { value: 'galaxy', label: { KR: '갤럭시', EN: 'Galaxy' }, icon: '✦', description: { KR: '은하수 효과', EN: 'Galaxy effect' } },
    { value: 'fireworks', label: { KR: '불꽃놀이', EN: 'Firework' }, icon: '✨', description: { KR: '불꽃놀이 효과', EN: 'Fireworks effect' } },
    { value: 'sparklers', label: { KR: '폭죽', EN: 'Sparkler' }, icon: '⚡', description: { KR: '폭죽 효과', EN: 'Sparkler effect' } },
    { value: 'burst', label: { KR: '폭발', EN: 'Burst' }, icon: '💥', description: { KR: '폭발 효과', EN: 'Burst effect' } },
    { value: 'fountain', label: { KR: '물방울 분수', EN: 'Water Fountain' }, icon: '⛲', description: { KR: '물방울 분수 효과', EN: 'Water droplet fountain' } },
    { value: 'flowerFountain', label: { KR: '꽃 분수', EN: 'Flower Fountain' }, icon: '🌸', description: { KR: '꽃잎이 흩날리는 분수', EN: 'Petals fountain effect' } },
    { value: 'leafFountain', label: { KR: '낙엽 분수', EN: 'Leaf Fountain' }, icon: '🍂', description: { KR: '낙엽이 날리는 분수', EN: 'Falling leaves fountain' } },
    { value: 'butterflyFountain', label: { KR: '나비 분수', EN: 'Butterfly Fountain' }, icon: '🦋', description: { KR: '나비가 춤추는 분수', EN: 'Dancing butterfly fountain' } },
    { value: 'noteFountain', label: { KR: '음표 분수', EN: 'Note Fountain' }, icon: '♪', description: { KR: '음표가 솟아나는 분수', EN: 'Musical notes fountain' } },
    { value: 'aquarium', label: { KR: '수족관', EN: 'Aquarium' }, icon: '🐠', description: { KR: '주파수별 물고기 헤엄치기', EN: 'Fish swimming by frequency' } },
    { value: 'wavyStaff', label: { KR: '파도 5선보', EN: 'Wavy Staff' }, icon: '♪', description: { KR: '파도치는 5선보 위의 춤추는 음표', EN: 'Dancing notes on wavy staff' } },
    { value: 'dancingFlowers', label: { KR: '춤추는 들꽃', EN: 'Dancing Flowers' }, icon: '🌸', description: { KR: '주파수별로 춤추는 흰색, 노란색, 분홍색 들꽃', EN: 'White, yellow, pink wildflowers dancing by frequency' } },
    { value: 'dancingButterfly', label: { KR: '춤추는 나비', EN: 'Dancing Butterfly' }, icon: '🦋', description: { KR: '꽃밭에서 음악에 맞춰 춤추며 날아다니는 하얀 나비와 노란 나비', EN: 'White and yellow butterflies dancing to music in flower garden' } },
    { value: 'vibration', label: { KR: '진동', EN: 'Vibration' }, icon: '📳', description: { KR: '음악 강도에 따른 화면 전체 진동 효과', EN: 'Full screen vibration effect based on music intensity' } }
  ];

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleSeekChange = (e) => {
    const seekTime = parseFloat(e.target.value);
    onSeek(seekTime);
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    onVolumeChange(newVolume);
  };

  const handleFftSizeChange = (e) => {
    const newFftSize = parseInt(e.target.value);
    onFftSizeChange(newFftSize);
  };

  return (
    <div className="control-panel">
      <div className="playback-controls">
        

        <div className="seek-container">
          <input
            type="range"
            min="0"
            max={duration}
            value={currentTime}
            onChange={handleSeekChange}
            className="seek-slider"
          />
        </div>
        
        <button 
          onClick={isPlaying ? onPause : onPlay}
          className="control-button play-pause"
        >
          {isPlaying ? <Pause size={24} /> : <Play size={24} />}
        </button>
        
        <button onClick={onStop} className="control-button stop">
          <Square size={20} />
        </button>

        <div className="time-display">
          {formatTime(currentTime)} / {formatTime(duration)}
        </div>
      </div>
      
      
      
      <div className="volume-container">
        <Volume2 size={20} />
        <input
          type="range"
          min="0"
          max="1"
          step="0.01"
          value={volume}
          onChange={handleVolumeChange}
          className="volume-slider"
        />
        <span className="volume-value">{Math.round(volume * 100)}%</span>

        <div className="setting-group">
            <label>
              {language === 'KR' ? 'FFT 크기:' : 'FFT Size:'}
              <select 
                value={fftSize} 
                onChange={handleFftSizeChange}
                className="setting-select"
              >
                <option value={512}>512</option>
                <option value={1024}>1024</option>
                <option value={2048}>2048</option>
                <option value={4096}>4096</option>
                <option value={8192}>8192</option>
              </select>
            </label>
          </div>
      </div>
      
      <div className="control-main-container">
        <div className="visualization-grid-container">
          <h4 className="visualization-title">
            {language === 'KR' ? '시각화 타입' : 'Visualization Type'}
          </h4>
          <div className="visualization-grid">
            {visualizationTypes.map(type => (
              <button
                key={type.value}
                onClick={() => onVisualizationTypeChange(type.value)}
                className={`viz-button ${visualizationType === type.value ? 'active' : ''}`}
                title={type.description[language] || type.description.EN}
              >
                <div className="viz-icon">{type.icon}</div>
                <span className="viz-label">{type.label[language] || type.label.EN}</span>
              </button>
            ))}
          </div>
        </div>
        
         
      </div>
    </div>
  );
};

export default ControlPanel;